/** Processing - the second place of a bill's journey (ADR-0212, Data in): To review, In progress,
 * Missing, Refused.
 *
 * Four views of one idea, each a table page (`TablePage`): a bill that was sent is stored, checked,
 * read into a draft record, and then **a person confirms** what it is - the scope and category, the
 * vendor and the site - and accepts it. Nothing in this place moves a document by itself.
 *
 * - **To review** (`?tab` absent): the bills that were read and wait for a person. A row opens the
 *   review, where the document and what was read from it sit side by side.
 * - **In progress** (`?tab=in-progress`): stored files not yet read, with the one act each can take.
 * - **Missing** (`?tab=missing`): the source census - what was expected and has not arrived, in its
 *   six states. `/sources` and `/coverage` redirect here.
 * - **Refused** (`?tab=refused`): files that were not opened, and files a person set aside.
 *
 * The census's own screen (its Sources, Coverage and Who bills what views) is still reachable at
 * `?tab=sources|coverage|billing` and is shown as it was, so nothing it could do is lost to the new
 * tabs. Missing is the table that replaces its coverage grid for the work of chasing a gap; the
 * register of who bills what moves to Vendors (another package's).
 */

import { useSearch } from "@tanstack/react-router";

import { RequireSession } from "../shell/RequireSession";
import { InProgress } from "./processing/InProgress";
import { Missing } from "./processing/Missing";
import { Refused } from "./processing/Refused";
import { ToReview } from "./processing/ToReview";
import { SourcesScreen } from "./Sources";

/** The census screen's own tab keys, kept so a link to one of them still opens that view. */
const CENSUS_VIEWS: readonly string[] = ["sources", "coverage", "billing"];

export function ProcessingScreen() {
  const search: { tab?: string } = useSearch({ strict: false });
  const tab = search.tab ?? "";
  if (CENSUS_VIEWS.includes(tab)) return <SourcesScreen />;
  return (
    <RequireSession>
      {tab === "in-progress" ? (
        <InProgress />
      ) : tab === "missing" ? (
        <Missing />
      ) : tab === "refused" ? (
        <Refused />
      ) : (
        <ToReview />
      )}
    </RequireSession>
  );
}
